import {
    ArrowRightEndOnRectangleIcon,
    PencilIcon,
    PlayIcon,
    SparklesIcon,
    CommandLineIcon,
    CubeTransparentIcon,
    RocketLaunchIcon,
} from "./automationStudioIcons";

// ---------------------------------------------------------------------------
// Labels & CTA text
// ---------------------------------------------------------------------------
export const LABELS = {
    // Action bar buttons
    GENERATE_AI_STEPS: 'Generate AI Steps',
    GENERATING_AI_STEPS: 'Generating AI Steps...',
    REPLAY: 'Replay',
    REPLAYING: 'Replaying...',
    SAVE_AND_REPLAY: 'Save and Replay',
    STOP_EXECUTION: 'Stop Execution',
    SAVE: 'Save',
    SAVING: 'Saving...',
    RESET: 'Reset',

    // Navigation / back buttons
    RESULTS: 'Results',
    INSTRUCTIONS: 'Instructions',

    // CTA buttons
    GET_STARTED: 'Get Started',
    EDIT_INSTRUCTIONS: 'Edit Instructions',
    RUN_AGAIN: 'Run Again',
    DELETE: 'Delete',
    ADD: 'Add',
    CANCEL: 'Cancel',

    // Tab labels
    TAB_TOOL_MODE: 'Tool & Mode',
    TAB_ARGUMENTS: 'Arguments',
    TAB_SELECTORS: 'Selectors',
    TAB_PREVIEW: 'Preview',
    TAB_VERIFICATION: 'Verification',
    TAB_META_INFO: 'Meta info',
    TAB_STEP_RESULTS: 'Step Results',
    TAB_TIMELINE: 'Timeline',

    // Step editor — Meta info tab (run info panel)
    META_SECTION_DYNAMIC_INTENT: 'Dynamic intent',
    META_SECTION_PARAMETER_BINDINGS: 'Parameter bindings',
    META_SECTION_INTENT_TOKENS: 'Intent tokens detected',
    META_COL_CATEGORY: 'Category',
    META_COL_TYPE: 'Type',
    META_COL_INTENT: 'Intent',
    META_COL_KEY: 'Key',
    META_COL_VALUE: 'Value',
    META_COL_STATUS: 'Status',
    META_COUNT_PARAM_SINGULAR: 'param',
    META_COUNT_PARAM_PLURAL: 'params',
    META_COUNT_TOKEN_SINGULAR: 'token',
    META_COUNT_TOKEN_PLURAL: 'tokens',
    META_NOT_BOUND: 'not bound',
    META_EMPTY_DYNAMIC_INTENT: 'No dynamic intent for this step.',
    /** Step numbers inside dynamic intent prose use engine stepIndex (0-based), not UI step numbers. */
    META_DYNAMIC_INTENT_ZERO_BASED_NOTE: 'Please note: Step indices within dynamic intent are currently zero-based.',
    META_EMPTY_BINDINGS: 'No parameter bindings for this step.',
    META_EMPTY_TOKENS: 'No intent tokens (source keys) for this step.',
    /** Shown in step header when this step id is listed in `dynamicResult.dynamicStepIds`. */
    STEP_DYNAMIC_BADGE: 'Dynamic',

    // Verification tab (replay + dynamic evaluateTestStep)
    VERIFICATION_EXPECTED_OUTCOME: 'Expected Outcome:',
    VERIFICATION_RECORDED_EXPECTED_OUTCOME: 'Recorded Expected Outcome:',
    VERIFICATION_RUNTIME_EXPECTED_OUTCOME: 'Runtime Expected Outcome:',

    // Agent trace (Preview tab)
    AGENT_TRACE_THINKING: 'Thinking',
    AGENT_TRACE_RESPONSE: 'Response',
    AGENT_TRACE_SUMMARY: 'Summary',

    // Panel section headers
    SECTION_INSTRUCTIONS: 'Instructions',
    SECTION_RECORDED_STEPS: 'Recorded Steps',
    SECTION_FUNCTION_LIBRARY: 'Function Library',
    SECTION_PARAMETERS: 'Parameters',

    // Empty / placeholder messages
    NO_INSTRUCTIONS: 'No instructions yet',
    NO_STEPS_RECORDED: 'No steps recorded yet',
    NO_REUSABLE_FUNCTIONS: 'No reusable functions',
    NO_PARAMETERS_DEFINED: 'No parameters defined',
    NO_EXECUTION_RESULTS: 'No execution results available',
    NO_STEP_SELECTED: 'No step selected',
    NO_VERIFICATION_DATA: 'No verification data available',
    NO_STEPS_GENERATED: 'No steps generated',

    // Metadata field labels
    DURATION: 'Duration:',
    TOOL: 'Tool:',
    CONFIDENCE: 'Confidence:',
    TOTAL_STEPS: 'Total Steps',
    SUCCESSFUL: 'Successful',
    FAILED_LABEL: 'Failed',
    SKIPPED_LABEL: 'Skipped',
    ALL_STEPS: 'All Steps',
    RECENT_LOGS: 'Recent Logs',

    // Step editor
    STEP_DESCRIPTION_PLACEHOLDER: 'Step description — type ${ for variables, @ for files',
    FAILURE_ERROR: 'Failure / Error',
    UNSAVED_CHANGES: 'unsaved changes',
    VERIFICATION_CHIP: 'VERIFICATION',

    // Verification tab
    PENDING_EXECUTION: 'Pending execution',
    RUN_TEST_HINT: 'Run the test to see verification result',
    VERIFICATION_PASSED: 'Verification Passed',
    VERIFICATION_FAILED: 'Verification Failed',
    VERIFICATION_METHOD: 'Verification Method',
    AI_EVALUATION_SMART: 'AI Evaluation (Smart)',
    ASSERTION_CODE_DIRECT: 'Assertion Code (Direct)',
    EXPECTED_OUTCOME: 'Expected Outcome',
    ACTUAL_OUTCOME: 'Actual Outcome',
    FAILURE_REASON: 'Failure Reason',
    DESCRIPTION: 'Description',
    METADATA: 'Metadata',

    // Running execution
    EXECUTING_AUTOMATION: 'Executing Automation...',
    CURRENTLY_EXECUTING_STEP: 'Currently Executing Step',
    LAST_EXECUTED_STEP: 'Last Executed Step',
    LAST_EXECUTED_STEP_WAITING: 'Waiting for a step to finish…',

    // Welcome / EmptyState
    WELCOME_TITLE: 'Welcome to Automation Studio',
    WELCOME_SUBTITLE: 'Your unified workspace for AI-assisted test automation',
    CTA_HINT: 'Start by writing your first instruction',

    // GettingStarted
    CREATE_FIRST_SCRIPT: 'Create Your First AI Script',
    WRITE_INSTRUCTIONS: 'Write Your Instructions',
    WRITE_INSTRUCTIONS_HINT: 'Describe what you want to test in plain English',
    QUICK_START_GUIDE: 'Quick Start Guide',

    // Execution results messages
    EXECUTION_SUCCESS: 'Execution Completed Successfully',
    EXECUTION_FAILED: 'Execution Failed',
    EXECUTION_PARTIAL: 'Execution Partially Completed',
    RECORDING_SKIP_NOTICE: 'This step will be skipped during replay',
    RECORDING_SKIP_REMOVE_ONLY: 'Recording did not complete this step successfully. You can review it here but cannot edit — remove it if you no longer need it.',

    // Select placeholders
    SELECT_ENVIRONMENT: 'Select Environment',
    SELECT_AI_MODEL: 'Select AI Model',
};

// ---------------------------------------------------------------------------
// Status colours
// ---------------------------------------------------------------------------
export const STATUS_COLORS = {
    success: '#16A34A',
    completed: '#16A34A',
    failed: '#DC2626',
    running: '#2563EB',
    pending: '#64748B',
    skipped: '#6B7280',
    default: '#64748B',
    // Background tints (used in chips / bordered boxes)
    successBg: '#DCFCE7',
    failedBg: '#FEE2E2',
};

// ---------------------------------------------------------------------------
// EmptyState feature cards
// ---------------------------------------------------------------------------
export const EMPTY_STATE_FEATURES = [
    {
        icon: SparklesIcon,
        title: 'AI-Powered Recording',
        description: 'Write instructions in plain English and let AI create test scripts for you',
    },
    {
        icon: CommandLineIcon,
        title: 'Visual Step Mapping',
        description: 'See how your instructions map to AI-generated test steps with collapsible hierarchy',
    },
    {
        icon: CubeTransparentIcon,
        title: 'Edit & Replay',
        description: 'Fine-tune selectors, customize steps, and replay tests—all in one place',
    },
    {
        icon: RocketLaunchIcon,
        title: 'Future Ready',
        description: 'Built for reusable functions, parameterization, and data-driven testing',
    },
];

// ---------------------------------------------------------------------------
// GettingStarted guide steps
// ---------------------------------------------------------------------------
export const GETTING_STARTED_STEPS = [
    {
        title: 'Write Instructions',
        description: 'Describe what you want to test in plain English',
    },
    {
        title: 'AI Generates Steps',
        description: 'Our AI converts your instructions into executable test steps',
    },
    {
        title: 'Review, Save & Run',
        description: 'Review steps, save and execute your test',
    },
];

// ---------------------------------------------------------------------------
// Empty right panel (no test case selected)
// ---------------------------------------------------------------------------
export const AUTOMATION_STUDIO_EMPTY_CONTENT = {
    badge: 'AI-powered · Playwright · Live execution',
    title: 'Write instructions. AI does the rest.',
    description:
        'Describe your test in plain English on the left — no code needed. The AI generates Playwright steps and executes them live, and streams screenshots back to you in real time.',
    examplePanelTitle: 'TRY AN EXAMPLE — CLICK TO USE',
    startAutomationLabel: 'Start Automation',
};

export const AUTOMATION_STUDIO_LOGIN_EXAMPLE = {
    id: 'loginFlow',
    label: 'Login flow',
    icon: ArrowRightEndOnRectangleIcon,
    instructions: [
        { text: 'Navigate to qa-path.com' },
        { text: 'Login with dummy-example@gmail.com and password' },
    ],
};

export const AUTOMATION_STUDIO_EMPTY_FLOW_STEPS = [
    {
        id: 'writeSteps',
        title: 'Write steps',
        description: 'Plain English instructions',
        icon: PencilIcon,
    },
    {
        id: 'aiGenerates',
        title: 'AI generates',
        description: 'Playwright steps created',
        icon: SparklesIcon,
    },
    {
        id: 'liveExecution',
        title: 'Live execution',
        description: 'Runs in browser, live',
        icon: PlayIcon,
    },
];
